import { Col, Form, message, Row, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Role } from "@rumble-it/common-lib/types/models/Role.interface";
import {
  createRole,
  fetchPermissions,
  fetchRolePresets,
} from "../../../lib/api/resources/roles";
import { RoleFormCard } from "./components/RoleFormCard";
import { RolePermissionsCard } from "./components/RolePermissionsCard";
import { useMemo, useState } from "react";

export type RoleFormValues = Omit<
  Role,
  "@id" | "id" | "servicePartners" | "dealership"
>;

export const RoleCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(
    [],
  );

  const permissionsQuery = useQuery({
    queryKey: ["permissions"],
    queryFn: fetchPermissions,
  });

  const presetsQuery = useQuery({
    queryKey: ["role-presets"],
    queryFn: fetchRolePresets,
  });

  const permissionIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const permission of permissionsQuery.data ?? []) {
      map.set(permission.name, permission.id);
    }
    return map;
  }, [permissionsQuery.data]);

  const applyPreset = (presetId: string) => {
    const preset = presetsQuery.data?.find((p) => p.id === presetId);
    if (!preset) {
      return;
    }

    const resolvedIds = preset.permissions
      .map((permissionName) => permissionIdByName.get(permissionName))
      .filter((id): id is string => id !== undefined);

    setSelectedPermissionIds(resolvedIds);
  };

  const createRoleMutation = useMutation({
    mutationFn: (data: RoleFormValues) => createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      message.success("Rol succesvol aangemaakt");
      navigate("/roles");
    },
    onError: () =>
      message.error("Er is een fout opgetreden bij het aanmaken van de rol"),
  });

  const handleFinish = (values: RoleFormValues) => {
    const payload = {
      name: values.name,
      externalSsoGroup: values.externalSsoGroup,
      pending: values.pending || false,
      dealershipRequired: values.dealershipRequired || false,
      system: values.system || false,
      permissions: selectedPermissionIds,
    };
    createRoleMutation.mutate(payload);
  };

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            label="Preset toepassen"
            validateStatus={
              presetsQuery.isError || permissionsQuery.isError
                ? "error"
                : undefined
            }
            help={
              presetsQuery.isError || permissionsQuery.isError
                ? "Presets konden niet worden geladen. Ververs de pagina en probeer het opnieuw."
                : undefined
            }
          >
            <Select
              allowClear
              placeholder="Kies een preset om permissies voor te selecteren"
              loading={presetsQuery.isLoading || permissionsQuery.isLoading}
              disabled={
                presetsQuery.isError ||
                permissionsQuery.isError ||
                presetsQuery.isLoading ||
                permissionsQuery.isLoading
              }
              onChange={(presetId?: string) => {
                if (presetId) {
                  applyPreset(presetId);
                }
              }}
              options={(presetsQuery.data ?? []).map((preset) => ({
                label: preset.name,
                value: preset.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xxl={{ span: 12 }} span={24}>
          <RoleFormCard
            form={form}
            title="Rol aanmaken"
            buttonText="Aanmaken"
            submitting={createRoleMutation.isPending}
            onFinish={handleFinish}
          />
        </Col>
        <Col xxl={{ span: 12 }} span={24}>
          <RolePermissionsCard
            editMode
            selectedPermissionIds={selectedPermissionIds}
            setSelectedPermissionIds={setSelectedPermissionIds}
          />
        </Col>
      </Row>
    </Form>
  );
};
